<script lang="ts" setup>
import { watch } from 'vue'
import { useUserStore } from '@/stores'
import {
  PaymentMethodSelector,
  createShowPaymentMethodSelector,
  useFixedBottomPayment,
  usePurchase,
} from '@/composables/payment'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'

defineProps<{
  config: any
}>()

const user = useUserStore()

// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplate,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = usePurchase('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// 判断内容高度并同步底部区域间距
const { isFixedBottom, containerRef: payCoverRef, checkContentHeight } = useFixedBottomPayment()

watch(currentPaymentMethod, () => {
  checkContentHeight()
})

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(templateProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

const { reportAddToCart } = useFbqAddToCart()
watch(templateProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Coins Package', p.price ?? 0)
})
</script>

<template>
  <div class="novelyn-pay-page">
    <BasicNavbar class="color-n-text-2">
      <div class="novelyn-nav-title">
        Store
      </div>
    </BasicNavbar>

    <!-- Cover -->
    <div ref="payCoverRef" class="pay-cover mb-24px mx-10px">
      <div class="novelyn-balance-card">
        <p class="novelyn-balance-label">
          MY BALANCE
        </p>
        <p class="flex items-end space-x-5px">
          <span class="novelyn-balance-value">{{ (user.$state.coinBalance ?? 0).toLocaleString('en-US') }}</span>
          <span class="novelyn-balance-unit">{{ config.texts.coins }}</span>
        </p>
      </div>

      <div :class="`pay-template pt-2px ${!isFixedBottom ? 'pb-0px' : 'pb-12px'}`">
        <!-- 充值 -->
        <div
          v-for="(item) in templateProducts"
          :key="item.productId"
          class="novelyn-pack-item"
          @click="PayTemplate(item)"
        >
          <div
            v-if="item.cornerTip && item.marketingText"
            class="novelyn-pack-corner"
            :class="{ 'is-lav': item.cornerTip === 2 }"
          >
            {{ item.marketingText }}
          </div>
          <img class="w-22px h-22px shrink-0" :src="config.resources.goldCoinIcon">
          <div class="flex-1 min-w-0">
            <div class="p-title">
              {{ item.coinsBuy }}<template v-if="!item.coinsBonus">
                {{ config.texts.coins }}
              </template>
              <span v-if="item.coinsBonus" class="p-bonus">+{{ item.coinsBonus }} {{ config.texts.coins }}</span>
            </div>
            <div v-if="item.cornerTip && item.coinsBonus" class="p-sub">
              +{{ parseFloat((item.coinsBonus / item.coinsBuy * 100).toFixed(2)) }}% {{ config.texts.coins }}
            </div>
          </div>
          <div class="p-price">
            $ {{ item.price }}
          </div>
        </div>
      </div>

      <!-- 支付方式选择器与 VIP 页保持一致，固定在底部 -->
      <div v-if="showPaymentMethodSelector" class="novelyn-pay-bar fixed bottom-0px left-0 right-0 z-50 flex justify-center">
        <PaymentMethodSelector
          variant="large"
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>

      <div class="novelyn-pay-tips pb-20px">
        <p class="tips-head">
          {{ config.texts.tips }}
        </p>
        <p>· {{ config.texts.tip1 }}</p>
        <p>· {{ config.texts.tip2 }}</p>
        <p>· {{ config.texts.tip3 }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pay-cover {
  min-height: calc(100vh - 68px);
  padding-top: 60px;
  margin-right: 16px;
  margin-left: 16px;
}

.novelyn-pay-page {
  min-height: 100dvh;
  padding-bottom: 96px;
  background: var(--novelyn-bg);
}

.novelyn-nav-title {
  position: absolute;
  inset: 0 58px;
  display: grid;
  place-items: center;
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-weight: 700;
  pointer-events: none;
}

.novelyn-balance-card {
  position: relative;
  padding: 20px;
  overflow: hidden;
  color: #fff;
  border-radius: 22px;
  background: var(--novelyn-gradient);
  box-shadow: 0 14px 30px rgba(224, 90, 126, .35);

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 14px;
    width: 44px;
    height: 44px;
    background: url('/images/novelyn/coin.svg') no-repeat center / contain;
    opacity: .35;
    transform: translateY(-50%);
  }
}

.novelyn-balance-label {
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  opacity: .85;
}

.novelyn-balance-value {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 32px;
  font-weight: 800;
}

.novelyn-balance-unit {
  padding-bottom: 5px;
  font-size: 14px;
  font-weight: 700;
}

/* 套餐行 */
.novelyn-pack-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
  margin-top: 10px;
  padding: 14px;
  border: 1.5px solid var(--novelyn-line);
  border-radius: 18px;
  background: #fff;
  box-shadow: var(--novelyn-shadow-sm);
  transition: border-color .18s ease, background .18s ease;

  &:active {
    border-color: var(--novelyn-rose);
    background: var(--novelyn-rose-faint);
    box-shadow: 0 8px 18px rgba(224, 90, 126, .18);
  }

  .p-title {
    font-size: 15px;
    font-weight: 900;
    color: var(--novelyn-ink);
  }

  .p-bonus {
    margin-left: 4px;
    font-size: 11px;
    font-weight: 900;
    color: var(--novelyn-lavender-deep);
  }

  .p-sub {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 800;
    color: var(--novelyn-ink-2);
  }

  .p-price {
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 900;
    color: var(--novelyn-rose-deep);
  }
}

/* HOT / BEST VALUE 角标 */
.novelyn-pack-corner {
  position: absolute;
  top: 0;
  right: 0;
  padding: 3px 10px;
  font-size: 9px;
  font-weight: 900;
  color: #fff;
  border-radius: 0 16px 0 12px;
  background: var(--novelyn-gradient);

  &.is-lav {
    background: var(--novelyn-lavender-gradient);
  }
}

/* tips */
.novelyn-pay-tips {
  padding-right: 16px;
  padding-left: 16px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.8;
  color: var(--novelyn-ink-3);

  p {
    margin-top: 6px;
  }

  .tips-head {
    font-size: 12px;
  }
}

/* 底部支付方式栏 */
.novelyn-pay-bar {
  width: 100%;
  background: rgba(255, 255, 255, .96);
  border-top: 1px solid var(--novelyn-line);
  backdrop-filter: blur(10px);

  :deep(.payment-method-selector-large) {
    gap: 10px;
    margin: 0;
    padding: 12px 16px 20px;
    background: transparent;
  }

  :deep(.payment-method-btn-large) {
    flex: 1 1 0;
    width: auto;
    height: auto;
    padding: 13px 0;
    border: 1.5px solid var(--novelyn-line);
    border-radius: 16px;
    background: #fff;
    box-shadow: var(--novelyn-shadow-sm);
  }

  :deep(.payment-method-btn-large.active),
  :deep(.payment-method-btn-large.active.paypal) {
    border-color: var(--novelyn-rose);
    background: var(--novelyn-rose-faint);
    box-shadow: 0 8px 18px rgba(224, 90, 126, .2);
  }
}
</style>
